import React, { useRef, useState } from 'react';
import {
  Card,
  CardContent,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Alert,
  AlertDescription,
} from '@roger/ui-components';
import {
  Upload,
  FileText,
  Image,
  CheckCircle,
  AlertCircle,
  Loader2,
  X,
  Download,
  File,
} from 'lucide-react';
import { useToast } from '../../../hooks/useToastContext';
// Import the function directly from sdk.gen
import { uploadClaimDocumentForPatientStediPatientsPatientIdClaimDocumentsPost } from '../../../services/api/sdk.gen';

export interface AttachmentInfo {
  id: string;
  attachmentControlNumber: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  reportTypeCode: string;
  description?: string;
  serviceLineNumber?: number;
  presignedUrl?: string;
  uploadStatus: 'idle' | 'uploading' | 'success' | 'error';
  uploadProgress?: number;
  error?: string;
}

interface AttachmentUploadCardProps {
  patientId: string;
  serviceLineNumber?: number;
  onUploadSuccess: (attachment: AttachmentInfo) => void;
  onRemove?: () => void;
  existingAttachment?: AttachmentInfo;
}

const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/tiff',
  'text/plain',
];
const MAX_SIZE_MB = 64;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

const reportTypeOptions = [
  { value: 'OZ', label: 'OZ - Support Data for Claim' },
  { value: 'DG', label: 'DG - Diagnostic Report' },
  { value: 'M1', label: 'M1 - Medical Record Attachment' },
  { value: 'CT', label: 'CT - Certification' },
  { value: '08', label: '08 - Plan of Treatment' },
  { value: 'MT', label: 'MT - Models' },
  { value: 'OB', label: 'OB - Operative Note' },
  { value: 'OC', label: 'OC - Oxygen Content Averaging Report' },
  { value: 'RB', label: 'RB - Radiology Films' },
  { value: 'RR', label: 'RR - Radiology Reports' },
];

export const AttachmentUploadCard: React.FC<AttachmentUploadCardProps> = ({
  patientId,
  serviceLineNumber,
  onUploadSuccess,
  onRemove,
  existingAttachment,
}) => {
  const { success: showSuccess, error: showError } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [reportTypeCode, setReportTypeCode] = useState('OZ'); // Default to OZ - Support Data for Claim
  const [description, setDescription] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <Image className='w-4 h-4' />;
    if (mimeType === 'application/pdf') return <FileText className='w-4 h-4' />;
    return <File className='w-4 h-4' />;
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setUploadError(
        'File type not allowed. Must be PDF, JPEG, PNG, TIFF, or TXT.'
      );
      showError('Invalid file type');
      return;
    }

    // Validate file size
    if (file.size > MAX_SIZE_BYTES) {
      setUploadError(`File too large. Maximum size is ${MAX_SIZE_MB}MB.`);
      showError('File too large');
      return;
    }

    setSelectedFile(file);
    setUploadError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      // Create the body object that matches the expected type
      // The formDataBodySerializer will convert this to FormData
      const body = {
        file: selectedFile,
        report_type_code: reportTypeCode,
        attachment_description: description || null,
        service_line_number:
          serviceLineNumber !== undefined ? serviceLineNumber : null,
        provider_control_number: null,
      };

      const response =
        await uploadClaimDocumentForPatientStediPatientsPatientIdClaimDocumentsPost(
          {
            path: { patient_id: patientId },
            body: body,
          }
        );

      if (response.data) {
        const attachmentInfo: AttachmentInfo = {
          id: response.data.id,
          attachmentControlNumber: response.data.attachment_control_number,
          fileName: response.data.file_name,
          fileSize: response.data.file_size,
          mimeType: response.data.mime_type,
          reportTypeCode: response.data.report_type_code,
          description: response.data.description || undefined,
          serviceLineNumber,
          presignedUrl: response.data.presigned_url || undefined,
          uploadStatus: 'success',
        };

        onUploadSuccess(attachmentInfo);
        showSuccess('File uploaded successfully');

        // Reset form
        setSelectedFile(null);
        setDescription('');
        setReportTypeCode('OZ');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadError('Failed to upload file. Please try again.');
      showError('Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = () => {
    if (existingAttachment?.presignedUrl) {
      window.open(existingAttachment.presignedUrl, '_blank');
    }
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove();
    }
  };

  // If we have an existing attachment, show it
  if (existingAttachment) {
    return (
      <Card className='border border-gray-200'>
        <CardContent className='p-4'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <div className='p-2 bg-green-50 rounded'>
                <CheckCircle className='w-5 h-5 text-green-600' />
              </div>
              <div className='flex-1'>
                <div className='flex items-center gap-2'>
                  {getFileIcon(existingAttachment.mimeType)}
                  <span className='font-medium text-sm'>
                    {existingAttachment.fileName}
                  </span>
                  <span className='text-xs text-gray-700'>
                    ({formatFileSize(existingAttachment.fileSize)})
                  </span>
                </div>
                <div className='text-xs text-gray-600 mt-1'>
                  Control #: {existingAttachment.attachmentControlNumber}
                  {existingAttachment.description && (
                    <span> • {existingAttachment.description}</span>
                  )}
                </div>
              </div>
            </div>
            <div className='flex items-center gap-2'>
              {existingAttachment.presignedUrl && (
                <Button
                  size='sm'
                  variant='ghost'
                  onClick={handleDownload}
                  title='Download'
                >
                  <Download className='w-4 h-4' />
                </Button>
              )}
              {onRemove && (
                <Button
                  size='sm'
                  variant='ghost'
                  onClick={handleRemove}
                  title='Remove'
                >
                  <X className='w-4 h-4' />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // New upload form
  return (
    <Card className='border border-dashed border-gray-300'>
      <CardContent className='p-4'>
        {!selectedFile ? (
          <div className='text-center'>
            <input
              ref={fileInputRef}
              type='file'
              accept={ALLOWED_TYPES.join(',')}
              onChange={handleFileChange}
              className='hidden'
            />
            <Button
              onClick={handleFileSelect}
              variant='outline'
              className='w-full'
              disabled={isUploading}
            >
              <Upload className='w-4 h-4' />
              Choose File
            </Button>
            <p className='text-xs text-gray-700 mt-2'>
              PDF, JPEG, PNG, TIFF, or TXT (max {MAX_SIZE_MB}MB)
            </p>
          </div>
        ) : (
          <div className='space-y-3'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                {getFileIcon(selectedFile.type)}
                <span className='text-sm font-medium'>{selectedFile.name}</span>
                <span className='text-xs text-gray-700'>
                  ({formatFileSize(selectedFile.size)})
                </span>
              </div>
              <Button
                size='sm'
                variant='ghost'
                onClick={() => {
                  setSelectedFile(null);
                  setDescription('');
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
              >
                <X className='w-4 h-4' />
              </Button>
            </div>

            <div className='grid grid-cols-2 gap-2'>
              <div>
                <label className='block text-xs font-medium mb-1'>
                  Report Type
                </label>
                <Select
                  value={reportTypeCode}
                  onValueChange={setReportTypeCode}
                >
                  <SelectTrigger className='h-8'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {reportTypeOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className='block text-xs font-medium mb-1'>
                  Description (optional)
                </label>
                <Input
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder='Brief description'
                  className='h-8'
                />
              </div>
            </div>

            {uploadError && (
              <Alert className='py-2'>
                <AlertCircle className='h-4 w-4' />
                <AlertDescription className='text-sm'>
                  {uploadError}
                </AlertDescription>
              </Alert>
            )}

            <Button
              onClick={handleUpload}
              disabled={isUploading}
              className='w-full'
              size='sm'
            >
              {isUploading ? (
                <>
                  <Loader2 className='w-4 h-4 animate-spin' />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className='w-4 h-4' />
                  Upload File
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AttachmentUploadCard;
